import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Shared filter row. The caller owns the state; debouncing the search term is
 * the caller's job too (useDebouncedValue) so the input stays responsive while
 * the query waits.
 */
export const FilterBar = ({
  search,
  onSearchChange,
  searchPlaceholder,
  selects = [],
  activeFilters = [],
  onClear,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-beam-blue/10 bg-white p-4 shadow-sm">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_repeat(auto-fit,minmax(150px,190px))]">
        <label className="relative block">
          <span className="sr-only">{searchPlaceholder}</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-beam-graphite/50"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            className="focus-ring w-full rounded-lg border border-beam-blue/10 py-3 pl-10 pr-4 text-sm font-semibold outline-none"
          />
        </label>

        {selects.map((select) => (
          <label key={select.id} className="grid gap-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-beam-graphite/55">
            {select.label}
            <select
              id={select.id}
              value={select.value}
              onChange={(event) => select.onChange(event.target.value)}
              disabled={select.disabled}
              className="focus-ring rounded-lg border border-beam-blue/10 bg-white px-3 py-2.5 text-sm font-bold normal-case tracking-normal text-beam-ink outline-none disabled:cursor-not-allowed disabled:bg-beam-light disabled:text-beam-graphite/45"
            >
              {select.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}

        {children}
      </div>

      {activeFilters.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <span
              key={filter.id}
              className="inline-flex items-center gap-1.5 rounded-md bg-beam-light px-2.5 py-1 text-xs font-bold text-beam-graphite"
            >
              {filter.label}
              {filter.onRemove ? (
                <button
                  type="button"
                  onClick={filter.onRemove}
                  className="focus-ring rounded text-beam-graphite/60 hover:text-state-critical"
                  aria-label={t('admin.removeFilter', { filter: filter.label })}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              ) : null}
            </span>
          ))}
          {onClear ? (
            <button
              type="button"
              onClick={onClear}
              className="focus-ring rounded-md px-2 py-1 text-xs font-extrabold text-beam-blue hover:bg-beam-light"
            >
              {t('admin.clearFilters')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
